import React, { useMemo } from "react";
import { Card, Box, Typography, useI18nContext } from "@procore/core-react";
import type { ColumnDefinition, NestedColumnDefinition, RowNode } from "@procore/data-table";
import { ClientSideDataTable } from "@procore/data-table";
import type {
   CompanyTotalsData,
   Breakdown,
} from "@laborchart-modules/lc-core-api/dist/api/totals/get-company-totals";
import styles from "./resource-breakdown-table.scss";
import classnames from "classnames";
import {
   buildBreakdownRows,
   extractPeriods,
   formatPeriodHeader,
   groupPeriodColumns,
   CATEGORY_ORDER,
   Category,
   Subcategory,
} from "./resource-breakdown-helpers";
import type { BreakdownRow } from "./resource-breakdown-helpers";

const cx = classnames.bind(styles);

const CATEGORY_LABEL_KEYS: Record<string, string> = {
   [Category.Assigned]: "row_assigned",
   [Category.Requested]: "row_requested",
   [Category.TimeOffMaintenance]: "row_time_off_maintenance",
   [Category.Total]: "row_total",
   [Category.Delta]: "row_delta",
};

const SUBCATEGORY_LABEL_KEYS: Record<string, string> = {
   [Subcategory.Labor]: "row_labor",
   [Subcategory.Equipment]: "row_equipment",
};

type ResourceBreakdownTableProps = {
   data: CompanyTotalsData | undefined;
   breakdown?: Breakdown;
   loading?: boolean;
};

function DeltaCellRenderer(props: { value: number; data: BreakdownRow; node: RowNode }) {
   const value = props.value ?? 0;
   const isDelta = props.node?.group
      ? props.node.aggData?.isDelta ?? false
      : props.data?.isDelta ?? false;

   const formatted = value % 1 === 0 ? String(value) : value.toFixed(2);

   if (isDelta) {
      const color = value > 0 ? "#27AE60" : value < 0 ? "#E74C3C" : undefined;
      const prefix = value > 0 ? "+" : "";
      return (
         <span style={{ color, fontWeight: 600 }}>
            {prefix}
            {formatted}
         </span>
      );
   }
   return <span>{formatted}</span>;
}

export const ResourceBreakdownTable = ({
   data,
   breakdown = "monthly",
   loading = false,
}: ResourceBreakdownTableProps) => {
   const I18n = useI18nContext();

   const rows = useMemo(() => {
      if (!data) return [];
      const raw = buildBreakdownRows(data);
      return raw.map((row) => {
         const key = SUBCATEGORY_LABEL_KEYS[row.subcategory];
         return key
            ? { ...row, subcategory: I18n.t(`views.company.workforce_planning.totals_page.${key}`) }
            : row;
      });
   }, [data, I18n]);
   const periods = useMemo(() => {
      if (!data) return [];
      return extractPeriods(data);
   }, [data]);

   const columnDefinitions: Array<ColumnDefinition | NestedColumnDefinition> = useMemo(() => {
      const periodCols: ColumnDefinition[] = periods.map((p) => ({
         field: p,
         headerName: formatPeriodHeader(p, breakdown, I18n.locale),
         sortable: false,
         suppressMenu: true,
         width: 120,
         cellRenderer: DeltaCellRenderer,
      }));

      const groupedPeriodCols = groupPeriodColumns(periodCols, periods, breakdown, I18n.locale);

      return [
         {
            field: "category",
            headerName: "",
            rowGroup: true,
            hidden: true,
            sortable: false,
            suppressMenu: true,
         },
         {
            field: "subcategory",
            headerName: "",
            hidden: true,
            sortable: false,
            suppressMenu: true,
         },
         ...groupedPeriodCols,
         {
            field: "total",
            headerName: I18n.t(`views.company.workforce_planning.totals_page.column_total`),
            sortable: false,
            suppressMenu: true,
            width: 120,
            pinned: "right" as const,
            cellRenderer: DeltaCellRenderer,
         },
      ];
   }, [periods, breakdown, I18n]);

   const autoGroupColumnDefinition: ColumnDefinition = useMemo(
      () => ({
         headerName: "",
         field: "subcategory",
         sortable: false,
         suppressMenu: true,
         width: 200,
         pinned: "left" as const,
         cellRendererParams: {
            suppressCount: true,
            innerRenderer: (params: { value: string }) => {
               const key = CATEGORY_LABEL_KEYS[params.value];
               return key
                  ? I18n.t(`views.company.workforce_planning.totals_page.${key}`)
                  : params.value;
            },
         },
      }),
      [I18n],
   );

   const getGroupRowAgg = useMemo(() => {
      return (nodes: RowNode[]) => {
         const agg: Record<string, number | boolean | string> = {};
         const periodKeys = [...periods, "total"];
         for (const key of periodKeys) {
            agg[key] = nodes.reduce(
               (sum: number, n: RowNode) => sum + ((n.data?.[key] as number) ?? 0),
               0,
            );
         }
         agg.isDelta = nodes[0]?.data?.isDelta ?? false;
         return agg as unknown as BreakdownRow;
      };
   }, [periods]);

   const groupOrderComparator = useMemo(() => {
      return (nodeA: RowNode, nodeB: RowNode) => {
         const indexA = CATEGORY_ORDER.indexOf(nodeA.key as (typeof CATEGORY_ORDER)[number]);
         const indexB = CATEGORY_ORDER.indexOf(nodeB.key as (typeof CATEGORY_ORDER)[number]);
         return indexA - indexB;
      };
   }, []);

   return (
      <Card>
         <Box padding="md">
            <Typography intent="h3">
               {I18n.t(`views.company.workforce_planning.totals_page.resource_breakdown_title`)}
            </Typography>
         </Box>
         <div className={cx("resourceBreakdownTable")}>
            <ClientSideDataTable
               columnDefinitions={columnDefinitions}
               getRowId={(params) => params.data?.id}
            >
               <ClientSideDataTable.Table
                  loading={loading}
                  rows={rows}
                  autoGroupColumnDefinition={autoGroupColumnDefinition}
                  getGroupRowAgg={getGroupRowAgg}
                  groupDefaultExpanded={-1}
                  groupIncludeFooter={false}
                  initialGroupOrderComparator={groupOrderComparator}
                  pinnedBottomRowData={[]}
                  UNSAFE_internalAGGridOverrides={{
                     suppressMovableColumns: true,
                  }}
               />
            </ClientSideDataTable>
         </div>
      </Card>
   );
};
